/**
 * Shared customer-facing core-return lifecycle labels/steps.
 * Mirrors api/src/utils/coreReturnLifecycle.ts + staff ERP queue stages.
 */

export const CORE_RETURN_LIFECYCLE_LABELS = {
  owed: "Core due",
  label_generated: "Label ready",
  in_transit: "In transit",
  received_pending_approval: "Received / inspection",
  approved: "Approved",
  forfeited: "Forfeited",
  closed: "Closed",
};

export function resolveCoreReturnLifecycleStatus(lifecycle = {}) {
  const fromApi = String(lifecycle?.coreReturnLifecycleStatus || "")
    .trim()
    .toLowerCase();
  if (fromApi && CORE_RETURN_LIFECYCLE_LABELS[fromApi]) return fromApi;

  const request = lifecycle?.activeReturnRequest || {};
  const requestStatus = String(
    request?.status || lifecycle?.activeReturnStatus || ""
  )
    .trim()
    .toLowerCase();
  const provider = String(
    request?.shippingProviderStatus || lifecycle?.activeReturnProviderStatus || ""
  )
    .trim()
    .toLowerCase();
  const hasLabel = !!(
    String(request?.shippingLabelUrl || lifecycle?.activeReturnLabelUrl || "").trim()
  );
  const hasTracking = !!(
    String(request?.trackingNumber || lifecycle?.activeReturnTrackingNumber || "").trim()
  );

  if (requestStatus === "forfeited" || lifecycle?.forfeitedAt) return "forfeited";
  if (lifecycle?.coreReturned || requestStatus === "approved" || requestStatus === "closed") {
    return "approved";
  }
  if (
    requestStatus === "received" ||
    requestStatus === "inspection_pending" ||
    provider === "delivered"
  ) {
    return "received_pending_approval";
  }
  if (provider === "in_transit" || provider === "pickup_scheduled") {
    return "in_transit";
  }
  if (
    requestStatus === "label_generated" ||
    requestStatus === "submitted" ||
    provider === "label_generated" ||
    hasLabel ||
    hasTracking
  ) {
    return "label_generated";
  }
  if (requestStatus === "requested" || lifecycle?.coreDue) return "owed";
  return "owed";
}

export function coreReturnLifecycleLabel(lifecycle, t = (key, fallback) => fallback || key) {
  const status = resolveCoreReturnLifecycleStatus(lifecycle);
  const defaults = CORE_RETURN_LIFECYCLE_LABELS;
  if (status === "forfeited") return t("coreReturn.status.forfeited", defaults.forfeited);
  if (status === "received_pending_approval") {
    return t("coreReturn.status.inspection", defaults.received_pending_approval);
  }
  if (status === "in_transit") return t("coreReturn.status.inTransit", defaults.in_transit);
  if (status === "label_generated") {
    return t("coreReturn.status.labelReady", defaults.label_generated);
  }
  if (status === "approved" || status === "closed") {
    return t("coreReturn.status.approved", defaults.approved);
  }
  if (lifecycle?.activeReturnRequest || lifecycle?.coreDue) {
    return status === "owed"
      ? lifecycle?.activeReturnRequest
        ? t("coreReturn.status.started", "Return started")
        : t("coreReturn.status.owed", defaults.owed)
      : t(`coreReturn.status.${status}`, defaults[status] || status);
  }
  return t("coreReturn.status.none", "No active return");
}

export function coreReturnWorkflowSteps(lifecycle, t = (key, fallback) => fallback || key) {
  const fromApi = Array.isArray(lifecycle?.coreReturnWorkflowSteps)
    ? lifecycle.coreReturnWorkflowSteps
    : null;
  if (fromApi?.length) {
    return fromApi.map((row) => ({
      ...row,
      label:
        row.key === "owed"
          ? t("coreReturn.timeline.started", "Return started")
          : row.key === "label_generated"
            ? t("coreReturn.timeline.label", "Label ready")
            : row.key === "in_transit"
              ? t("coreReturn.timeline.transit", "In transit")
              : row.key === "received_pending_approval"
                ? t("coreReturn.timeline.received", "Received / inspection")
                : row.key === "approved"
                  ? t("coreReturn.timeline.approved", "Approved")
                  : row.key === "forfeited"
                    ? t("coreReturn.status.forfeited", "Forfeited")
                    : row.label,
    }));
  }

  const status = resolveCoreReturnLifecycleStatus(lifecycle);
  if (status === "forfeited") {
    return [
      { key: "owed", label: t("coreReturn.timeline.started", "Return started"), done: true, current: false },
      { key: "forfeited", label: t("coreReturn.status.forfeited", "Forfeited"), done: true, current: true },
    ];
  }
  const order = [
    "owed",
    "label_generated",
    "in_transit",
    "received_pending_approval",
    "approved",
  ];
  const labels = {
    owed: t("coreReturn.timeline.started", "Return started"),
    label_generated: t("coreReturn.timeline.label", "Label ready"),
    in_transit: t("coreReturn.timeline.transit", "In transit"),
    received_pending_approval: t("coreReturn.timeline.received", "Received / inspection"),
    approved: t("coreReturn.timeline.approved", "Approved"),
  };
  const currentIndex = Math.max(0, order.indexOf(status));
  return order.map((key, index) => ({
    key,
    label: labels[key],
    done: index < currentIndex || (key === "approved" && status === "approved"),
    current: key === status,
  }));
}
